import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  AiStarFilled,
  Edit,
  OverflowMenuVertical,
  StatusChange,
  TrashCan,
} from '@mint-lib/icons';
import {
  Button,
  Divider,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { useAiHubPermissions } from '../../../../hooks/useAiHubPermissions.jsx';

type Props = {
  onRename: () => void;
  onDelete?: () => void;
  onMove: () => void;
  className?: string;
};

const FolderActionsCell = ({
  onRename,
  onDelete,
  className,
  onMove,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { canUploadKBFile } = useAiHubPermissions();
  const [open, setOpen] = useState(false);

  if (!canUploadKBFile) return;

  const handleRename = () => {
    onRename();
    setOpen(false);
  };

  const handleDelete = () => {
    onDelete?.();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          icon
          size="small"
          variant="plain"
          aria-label="folder-actions-button"
          data-dd-action-name="Knowledge base: open folder actions"
          className={cn(
            'flex h-full min-w-6 flex-row items-center justify-center',
            className,
          )}
          onClick={() => setOpen(true)}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <OverflowMenuVertical />
        </Button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          side="bottom"
          sideOffset={5}
          align="end"
          className="relative z-popover h-full w-max max-w-64 min-w-28.5 rounded-lg data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
        >
          <Button
            size={'small'}
            variant={'text'}
            className="h-10 w-full items-center justify-start gap-2 pl-3"
            //onClick={() => {}}
            aria-label="ask-mintiq-folder"
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <AiStarFilled className="text-text-primary" />
            <p
              className={typographyVariants({
                variant: 'bodyShort01',
                color: 'primary',
              })}
            >
              {t('Ask MintIQ')}
            </p>
          </Button>
          <Button
            size={'small'}
            variant={'text'}
            className="h-10 w-full items-center justify-start gap-2 pl-3"
            aria-label="rename-folder"
            data-dd-action-name="Knowledge base: rename folder"
            onClick={handleRename}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <Edit className="fill-text-primary" />
            <p
              className={typographyVariants({
                variant: 'bodyShort01',
                color: 'primary',
              })}
            >
              {t('Rename')}
            </p>
          </Button>
          <Button
            size={'small'}
            variant={'text'}
            className="h-10 w-full items-center justify-start gap-2 pl-3"
            onClick={onMove}
            aria-label="move-folder"
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <StatusChange className="fill-text-primary" />
            <p
              className={typographyVariants({
                variant: 'bodyShort01',
                color: 'primary',
              })}
            >
              {t('Move to')}
            </p>
          </Button>
          {onDelete && (
            <>
              <Divider />
              <Button
                size={'small'}
                variant={'text'}
                className="h-10 w-full items-center justify-start gap-2 pl-3"
                aria-label="delete-folder"
                data-dd-action-name="Knowledge base: delete folder"
                onClick={handleDelete}
                onDoubleClick={(e) => e.stopPropagation()}
              >
                <TrashCan className="fill-error-dark" />
                <p
                  className={cn(
                    typographyVariants({ variant: 'bodyShort01' }),
                    'text-error-dark',
                  )}
                >
                  {t('Delete')}
                </p>
              </Button>
            </>
          )}
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export { FolderActionsCell };
